import {
  useGetConsultantContractsQuery,
  useDeleteConsultantContractMutation,
  GetConsultantContracts
} from "@bonliva-crm/graphql";
import { Paper, Typography } from "@material-ui/core";
import { Loader } from "@bonliva-crm/ui";
import { useState } from "react";
import { makeStyles } from "@material-ui/styles";
import { ModalProvider } from "./modal-provider";
import { ActionUnion, ContractUnion } from "../types";
import { useLocale, useSnackbars } from "@hooks";
import { ConsultantContractsTable } from "./consultant-contracts-table";

type Props = {
  consultantId: string;
  handleNavigationAction: (action: ActionUnion, contractId?: string) => void;
};

export const ConsultantContracts = (props: Props) => {
  const [openContractMoreList, setOpenContractMoreList] = useState(null);
  const [action, setAction] = useState(null);
  const [chosenContract, setChosenContract] = useState<ContractUnion | null>(null);
  const { showError, showSuccess } = useSnackbars();
  const classes = useStyles();
  const { locale } = useLocale();

  const query = useGetConsultantContractsQuery({
    variables: {
      id: props.consultantId
    },
    fetchPolicy: "network-only"
  });

  const [deleteConsultantContractMutation] = useDeleteConsultantContractMutation({
    onCompleted: () => {
      showSuccess(locale.deleteContractSuccess);
    },
    onError: (error) => {
      console.error(error);
      showError(locale.cannotPerformAction);
    },
    refetchQueries: [GetConsultantContracts.OperationName]
  });

  const handleDelete = (id: string) => {
    deleteConsultantContractMutation({
      variables: {
        contractId: id
      },
      optimisticResponse: {
        deleteConsultantContract: {
          _id: id,
          __typename: "ConsultantContract"
        }
      }
    });
  };

  if (query.loading && !query.data) {
    return <Loader />;
  }

  if (query.data && query.data.getConsultantContracts.length === 0) {
    return (
      <Paper className={classes.paperStyle}>
        <Typography variant="body2" className={classes.margin16}>
          {locale.NoConsultantContracts}
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Typography variant="h5" className={classes.margin16}>
        {locale.ConsultantContracts}
      </Typography>
      <ConsultantContractsTable
        contracts={
          query.data?.getConsultantContracts.filter((c) =>
            !c.EndDate ? true : new Date(c.EndDate) > new Date()
          ) || []
        }
        openContractMoreList={openContractMoreList}
        setOpenContractMoreList={setOpenContractMoreList}
        setAction={setAction}
        setChosenContract={setChosenContract}
        handleNavigationAction={props.handleNavigationAction}
      />
      <Typography variant="h5" className={classes.margin16}>
        {locale.Archived}
      </Typography>
      <ConsultantContractsTable
        defaultCollapsedState
        contracts={
          query.data?.getConsultantContracts.filter((c) =>
            !c.EndDate ? false : new Date(c.EndDate) < new Date()
          ) || []
        }
        openContractMoreList={openContractMoreList}
        setOpenContractMoreList={setOpenContractMoreList}
        setAction={setAction}
        setChosenContract={setChosenContract}
        handleNavigationAction={props.handleNavigationAction}
      />
      <ModalProvider
        handleDelete={handleDelete}
        open={!!action}
        setOpen={() => setAction("")}
        action={action}
        contracts={query.data?.getConsultantContracts}
        contract={chosenContract}
        consultantId={props.consultantId}
      />
    </>
  );
};

const useStyles = makeStyles(() => ({
  margin16: {
    marginTop: 16,
    marginBottom: 16
  },
  paperStyle: {
    marginTop: 16,
    width: "100%",
    height: "190px",
    borderRadius: "2px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center"
  }
}));
